import { useEffect, useMemo, useState } from 'react'

export const ACK_SLA_MS = 15 * 60 * 1000 // 15 minutes

/** Short duration, e.g. "0:15:00", "2:05:30", "2d 3:05:30" */
export function formatHumanDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000))
  const days = Math.floor(totalSeconds / 86_400)
  const hours = Math.floor((totalSeconds % 86_400) / 3_600)
  const minutes = Math.floor((totalSeconds % 3_600) / 60)
  const seconds = totalSeconds % 60
  const time = `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  return days >= 1 ? `${days}d ${time}` : time
}

export function useSlaTimer(requestedAtIso: string, acknowledgeDueAtIso?: string | null) {
  const dueAtMs = useMemo(() => {
    if (acknowledgeDueAtIso) {
      const due = new Date(acknowledgeDueAtIso).getTime()
      if (!Number.isNaN(due)) return due
    }
    const requestedAtMs = new Date(requestedAtIso).getTime()
    return requestedAtMs + ACK_SLA_MS
  }, [requestedAtIso, acknowledgeDueAtIso])

  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [])

  const remaining = dueAtMs - now
  const isExpired = remaining <= 0
  const remainingText = formatHumanDuration(Math.abs(remaining))

  return {
    remainingMs: remaining,
    overdueMs: isExpired ? Math.abs(remaining) : 0,
    remainingText,
    isExpired,
    isCritical: remaining > 0 && remaining <= 60_000, // last 1 minute
  }
}

/**
 * Elapsed time since startedAt. When endedAt is provided, freezes at that endpoint
 * (no live ticking). Otherwise ticks every second.
 */
export function useElapsedTimer(startedAtIso: string, endedAtIso?: string | null) {
  const startedAtMs = useMemo(() => {
    const t = new Date(startedAtIso).getTime()
    return Number.isNaN(t) ? null : t
  }, [startedAtIso])

  const endedAtMs = useMemo(() => {
    if (!endedAtIso) return null
    const t = new Date(endedAtIso).getTime()
    return Number.isNaN(t) ? null : t
  }, [endedAtIso])

  const isFrozen = endedAtMs != null

  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (isFrozen) return
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [isFrozen])

  const endMs = isFrozen ? endedAtMs! : now
  const elapsedMs = startedAtMs == null ? 0 : Math.max(0, endMs - startedAtMs)

  return {
    elapsedMs,
    elapsedText: formatHumanDuration(elapsedMs),
    isFrozen,
  }
}
